// Custom hook for managing QDF filtering and pagination
import { useState, useMemo, useEffect } from 'react';

export const useQDFFiltering = (entries, itemsPerPage = 5) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(Number(sessionStorage.getItem('qdf-currentPage')) || 1);
  const [activeTab, setActiveTab] = useState('all');

  // Main filtering logic
  const getFilteredEntries = useMemo(() => {
    const getPPMEntries = () => {
      return entries.filter(entry => 
        entry.maintenanceType && 
        entry.maintenanceType.toLowerCase() === 'preventive'
      );
    };

    const getSREntries = () => {
      return entries.filter(entry => 
        entry.maintenanceType && 
        entry.maintenanceType.toLowerCase() === 'corrective'
      );
    };

    const getCompletedPPMEntries = () => {
      return getPPMEntries().filter(entry => 
        entry.scStatus && 
        (entry.scStatus.toLowerCase().includes('completed') || 
         entry.scStatus.toLowerCase().includes('closed') ||
         entry.scStatus.toLowerCase().includes('resolved'))
      );
    };

    const getActivePPMEntries = () => {
      return getPPMEntries().filter(entry => 
        entry.scStatus && 
        entry.scStatus.toLowerCase().includes('active')
      );
    };

    const getCompletedSREntries = () => {
      return getSREntries().filter(entry => 
        entry.scStatus && 
        (entry.scStatus.toLowerCase().includes('completed') || 
         entry.scStatus.toLowerCase().includes('closed') ||
         entry.scStatus.toLowerCase().includes('resolved'))
      );
    };

    const getActiveSREntries = () => {
      return getSREntries().filter(entry => 
        entry.scStatus && 
        entry.scStatus.toLowerCase().includes('active')
      );
    };

    let filtered = [];
    
    // Apply tab filtering first
    switch (activeTab) {
      case 'ppm-active':
        filtered = getActivePPMEntries();
        break;
      case 'ppm-completed':
        filtered = getCompletedPPMEntries();
        break;
      case 'sr-active':
        filtered = getActiveSREntries();
        break;
      case 'sr-completed':
        filtered = getCompletedSREntries();
        break;
      case 'all-ppm':
        filtered = getPPMEntries();
        break;
      case 'all-sr':
        filtered = getSREntries();
        break;
      default:
        filtered = entries;
    }
    
    // Apply search filtering
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(entry => 
        (entry.scNumber && entry.scNumber.toLowerCase().includes(searchLower)) ||
        (entry.location && entry.location.toLowerCase().includes(searchLower)) ||
        (entry.assetNumber && entry.assetNumber.toLowerCase().includes(searchLower)) ||
        (entry.scStatus && entry.scStatus.toLowerCase().includes(searchLower)) ||
        (entry.maintenanceType && entry.maintenanceType.toLowerCase().includes(searchLower)) ||
        (entry.ppmType && entry.ppmType.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply date filtering
    if (startDate || endDate) {
      filtered = filtered.filter(entry => {
        if (!entry.createdAt) return false;
        
        const entryDate = new Date(entry.createdAt);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;
        
        if (start && end) {
          return entryDate >= start && entryDate <= end;
        } else if (start) {
          return entryDate >= start;
        } else if (end) {
          return entryDate <= end;
        }
        
        return true;
      });
    }
    
    return filtered;
  }, [entries, activeTab, searchTerm, startDate, endDate]);

  // Pagination logic
  const getPaginatedEntries = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return getFilteredEntries.slice(startIndex, endIndex);
  }, [getFilteredEntries, currentPage, itemsPerPage]);

  const totalPages = useMemo(() => Math.ceil(getFilteredEntries.length / itemsPerPage), [getFilteredEntries.length, itemsPerPage]);

  // Keep the current page in range when filtering/status updates reduce results.
  useEffect(() => {
    const safeTotalPages = totalPages > 0 ? totalPages : 1;

    if (currentPage > safeTotalPages) {
      setCurrentPage(safeTotalPages);
      sessionStorage.setItem('qdf-currentPage', String(safeTotalPages));
      return;
    }

    if (currentPage < 1) {
      setCurrentPage(1);
      sessionStorage.setItem('qdf-currentPage', '1');
    }
  }, [currentPage, totalPages]);

  const getStatusCounts = () => {
    const ppmEntries = entries.filter(entry => 
      entry.maintenanceType && 
      entry.maintenanceType.toLowerCase() === 'preventive'
    );
    const srEntries = entries.filter(entry => 
      entry.maintenanceType && 
      entry.maintenanceType.toLowerCase() === 'corrective'
    );
    const activePPM = ppmEntries.filter(entry => 
      entry.scStatus && 
      entry.scStatus.toLowerCase().includes('active')
    );
    const completedPPM = ppmEntries.filter(entry => 
      entry.scStatus && 
      (entry.scStatus.toLowerCase().includes('completed') || 
       entry.scStatus.toLowerCase().includes('closed') ||
       entry.scStatus.toLowerCase().includes('resolved'))
    );
    const activeSR = srEntries.filter(entry => 
      entry.scStatus && 
      entry.scStatus.toLowerCase().includes('active')
    );
    const completedSR = srEntries.filter(entry => 
      entry.scStatus && 
      (entry.scStatus.toLowerCase().includes('completed') || 
       entry.scStatus.toLowerCase().includes('closed') ||
       entry.scStatus.toLowerCase().includes('resolved'))
    );

    return {
      total: entries.length,
      totalPPM: ppmEntries.length,
      totalSR: srEntries.length,
      activePPM: activePPM.length,
      completedPPM: completedPPM.length,
      activeSR: activeSR.length,
      completedSR: completedSR.length
    };
  };

  // Event handlers
  const handleSearchChange = (e, skipPageReset = false) => {
    setSearchTerm(e.target.value);
    if (!skipPageReset) {
      setCurrentPage(1);
      sessionStorage.setItem('qdf-currentPage', '1');
    }
  };

  const handleStartDateChange = (e, skipPageReset = false) => {
    setStartDate(e.target.value);
    if (!skipPageReset) {
      setCurrentPage(1);
      sessionStorage.setItem('qdf-currentPage', '1');
    }
  };

  const handleEndDateChange = (e, skipPageReset = false) => {
    setEndDate(e.target.value);
    if (!skipPageReset) {
      setCurrentPage(1);
      sessionStorage.setItem('qdf-currentPage', '1');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    sessionStorage.setItem('qdf-currentPage', page);
    
    // Scroll to the bottom where pagination is (like viewReports.js)
    setTimeout(() => {
      window.scrollTo(0, document.documentElement.scrollHeight);
    }, 0);
  };

  const setPageSilently = (page) => {
    console.log('🔧 setPageSilently called with page:', page);
    setCurrentPage(page);
    sessionStorage.setItem('qdf-currentPage', page);
    console.log('🔧 setPageSilently completed');
    // No scroll behavior for silent page changes
  };

  const handleTabChange = (tab, skipPageReset = false) => {
    setActiveTab(tab);
    if (!skipPageReset) {
      setCurrentPage(1);
      sessionStorage.setItem('qdf-currentPage', '1');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    sessionStorage.setItem('qdf-currentPage', '1');
  };

  return {
    // State
    searchTerm,
    startDate,
    endDate,
    currentPage,
    activeTab,
    
    // Computed values
    filteredEntries: getFilteredEntries,
    paginatedEntries: getPaginatedEntries,
    totalPages,
    statusCounts: getStatusCounts(),
    
    // Event handlers
    handleSearchChange,
    handleStartDateChange,
    handleEndDateChange,
    handlePageChange,
    setPageSilently,
    handleTabChange,
    clearFilters
  };
};
